import { createSelector } from '@reduxjs/toolkit';
import ms from 'ms';

import { GlobalState } from '@/redux/store';

import { NO_SIMILAR_ITEM_ALERT } from './similarItemAlerts.types';

const similarItemAlertsSlice = (state: GlobalState) => state.similarItemAlerts;

type LotId = number;
const lotIdSelector = (_: GlobalState, lotId: LotId) => lotId;

const byIdSelector = createSelector(similarItemAlertsSlice, ({ byId }) => byId);
const loadedSelector = createSelector(similarItemAlertsSlice, ({ loaded }) => loaded);
const loadingSelector = createSelector(similarItemAlertsSlice, ({ loading }) => loading);
const savingSelector = createSelector(similarItemAlertsSlice, ({ saving }) => saving);

const listSelector = createSelector(similarItemAlertsSlice, ({ list }) => list);

export const getSimilarItemAlertsListItems = createSelector(listSelector, ({ items }) => items);

// The count for the current query — filtered once a keyword is active. Drives the pager.
export const getSimilarItemAlertsTotalCount = createSelector(listSelector, ({ totalRecords }) => totalRecords);

// The bidder's total alert count, independent of any keyword. Feeds the search placeholder and the
// empty-state check so neither shrinks toward zero as the bidder narrows the list.
export const getSimilarItemAlertsUnfilteredCount = createSelector(
    listSelector,
    ({ unfilteredTotalRecords }) => unfilteredTotalRecords
);

export const getSimilarItemAlertsListPage = createSelector(listSelector, ({ page }) => page);

export const getSimilarItemAlertsListPageSize = createSelector(listSelector, ({ pageSize }) => pageSize);

export const isSimilarItemAlertsListLoading = createSelector(listSelector, ({ status }) => status === 'loading');

// True only once a fetch has returned zero rows — not before the first load — so the section never
// flashes its empty state while the initial request is still idle or in flight. Keyed on the
// *unfiltered* total so a no-match search shows "nothing matched", not the zero-alerts onboarding.
export const isSimilarItemAlertsListEmpty = createSelector(
    listSelector,
    ({ status, unfilteredTotalRecords }) => status === 'success' && unfilteredTotalRecords === 0
);

export const getSimilarItemAlertsLotIds = createSelector(getSimilarItemAlertsListItems, (items) =>
    items.map((item) => item.lotId)
);

export const getSimilarItemAlertId = createSelector(
    [byIdSelector, lotIdSelector],
    (byId, lotId) => byId[lotId] ?? NO_SIMILAR_ITEM_ALERT
);

export const getIsSimilarItemAlertSet = createSelector(
    [getSimilarItemAlertId],
    (savedItemId) => savedItemId !== NO_SIMILAR_ITEM_ALERT
);

export const isSimilarItemAlertLoading = createSelector([loadingSelector, lotIdSelector], (loading, lotId) =>
    loading.includes(lotId)
);

export const isSimilarItemAlertSaving = createSelector([savingSelector, lotIdSelector], (saving, lotId) =>
    saving.includes(lotId)
);

const getLoadTimeForSimilarItemAlert = createSelector(
    [loadedSelector, lotIdSelector],
    (loaded, lotId) => loaded[lotId] || 0
);

export const SIMILAR_ITEM_ALERTS_SLICE_CACHE_TIME = ms('5m');

export const shouldFetchSimilarItemAlert = (state: GlobalState, lotId: LotId): boolean => {
    if (!(lotId > 0)) {
        return false;
    }
    const loaded = getLoadTimeForSimilarItemAlert(state, lotId);
    if (loaded && Date.now() - loaded < SIMILAR_ITEM_ALERTS_SLICE_CACHE_TIME) {
        return false;
    }
    // A set/remove in flight is about to write a newer answer than this lookup could.
    if (isSimilarItemAlertSaving(state, lotId)) {
        return false;
    }
    return !isSimilarItemAlertLoading(state, lotId);
};
